// ============================================================
// Jede Route des Backends genau einmal, mit ihrem Rückgabetyp — und darüber
// die Abfrage- und Änderungshaken, die die Bildschirme benutzen.
//
// Die Bildschirme sollen keine Pfade kennen. Wandert eine Route, ändert sich
// DIESE Datei, und der Übersetzer zeigt jeden Aufrufer, der nicht mitkommt.
// Die Reihenfolge folgt der Tabelle in SPEC §3; alle Pfade stehen OHNE das
// Präfix `/api` (das setzt client.ts, und Traefik schneidet es vor dem
// Backend wieder ab).
//
// ---- Drei Regeln, die hier durchgehalten werden --------------------------
//
// 1. SERVERDATEN GEHÖREN TANSTACK QUERY, OBERFLÄCHENZUSTAND ZUSTAND
//    (SPEC §7). Es gibt hier keinen zweiten Speicher mit Projekten darin.
//
// 2. NACH JEDER ÄNDERUNG WIRD `['stand']` UND DAS PROJEKT ENTWERTET.
//    `GET /stand` ist die eine Antwort, aus der Karte, Board, Matrix und
//    Büroseiten leben (SPEC §3) — wer eine Abgabe verschiebt und nur das
//    Projekt entwertet, sieht auf der Karte weiter den alten Countdown.
//    Deshalb steht das Entwerten in EINER Hilfsfunktion und nicht je Haken.
//
// 3. EIN 409 SCHREIBT DEN MITGELIEFERTEN PROJEKTSTAND IN DEN ZWISCHENSPEICHER
//    UND WIRFT WEITER. Das Backend legt bei einer veralteten `version` den
//    aktuellen `Projekt` in den Fehlerkörper (SPEC §2). Ihn sofort abzulegen
//    heißt: der Bildschirm zeigt beim Weiterwerfen schon den neuen Stand, und
//    die Meldung „jemand anderes war schneller" steht vor Daten, die stimmen.
//    Die `version` wird dabei NIE stillschweigend übernommen und der Aufruf
//    nicht wiederholt — das wäre die optimistische Sperre ausgehebelt.
// ============================================================
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api, frage, istApiFehler } from "./client";
import { KEINE_HANDLUNG } from "./startseite";
import type {
  Abgabe,
  AbgabeEingabe,
  Termin,
  TerminAendern,
  TerminEingabe,
  AdminNutzer,
  AdminNutzerAendern,
  AdminNutzerNeu,
  AktuellesSeite,
  AuthConfig,
  AuthStart,
  AuthVerify,
  Beitrag,
  BeitragAendern,
  BeitragArt,
  BeitragKurz,
  BeitragNeu,
  Bereich,
  BereichEingabe,
  BereichEintrag,
  Bild,
  BildAendern,
  Buero,
  BueroEingabe,
  GrenzEbeneWaehlen,
  GrenzImport,
  GrenzUebernehmen,
  GrenzeSetzen,
  Ich,
  IchAendern,
  KommentarNeu,
  LonLat,
  Konfig,
  KonzeptTeilAendern,
  LeitungUebersicht,
  KonzeptTeilNeu,
  OeffentlichBeitrag,
  OeffentlichProjektDetail,
  OeffentlichStand,
  Ortstreffer,
  Phase,
  PhaseEingabe,
  Projekt,
  ProjektAendern,
  MonatswertSetzen,
  ProjektLphSetzen,
  ProjektNeu,
  ProjektStand,
  ReaktionArt,
  Reaktionen,
  ReaktionSetzen,
  Richtwert,
  RichtwertEingabe,
  Abwesenheit,
  Runde,
  RundeKurz,
  RundePunkt,
  RundePunktAendern,
  RundePunktNeu,
  Stand,
  StandListe,
  Verlauf,
  Wunsch,
  WunschAendern,
  WunschNeu,
} from "./types";
import { useUi } from "../store/ui";

/* ========================================================== Schlüssel */

export const schluessel = {
  stand: ["stand"] as const,
  projekt: (id: string) => ["projekt", id] as const,
  /** Die Leiter EINES Projekts (015, §18.2). Bewusst UNTER `projekt`: `entwerten`
   *  entwertet den Präfix `["projekt", id]` und erwischt sie damit mit — eine
   *  Änderung am Auftragsbuch ändert auch den Kopf der Akte. */
  projektStand: (id: string, monat?: string) => ["projekt", id, "stand", monat ?? ""] as const,
  /** Die Standliste ALLER Projekte je Monat (§23). */
  staende: (monat?: string) => ["staende", monat ?? ""] as const,
  /** Präfix für ALLE Monate der Standliste — zum Entwerten (Fehlerrunde 01.10.2026, #17). */
  staendeAlle: ["staende"] as const,
  /** Der Verlauf EINES Projekts (§20.3) — ebenfalls unter `projekt`. */
  projektVerlauf: (id: string) => ["projekt", id, "verlauf"] as const,
  /** Die Marken auf dem Leistungsregler — Stammdaten, ohne Projektbezug. */
  richtwerte: ["richtwerte"] as const,
  /** Die Funktionswünsche des Hauses (§21). */
  wuensche: ["wuensche"] as const,
  /** Das Blatt der Geschäftsführung (§18.6). */
  leitung: ["leitung"] as const,
  /** Präfix für ALLE Runden-Abfragen — zum Entwerten in einem Griff (§18.5). */
  rundenAlle: ["runden"] as const,
  runden: (bueroId?: string, status?: string) =>
    ["runden", "liste", bueroId ?? "", status ?? ""] as const,
  runde: (id: string) => ["runden", "eine", id] as const,
  /** Meine nächste Runde — die Vorbereitung. */
  meineRunde: ["runden", "meine"] as const,
  abwesenheiten: (von?: string, bis?: string) =>
    ["abwesenheiten", von ?? "", bis ?? ""] as const,
  meineAbwesenheit: ["abwesenheiten", "meine"] as const,
  konfig: ["konfig"] as const,
  ich: ["ich"] as const,
  /** Präfix für ALLE Zeitfenster — zum Entwerten. */
  abgabenAlle: ["abgaben"] as const,
  /** §40.2: alle Terminlisten (Präfix, wie bei den Abgaben). */
  termineAlle: ["termine"] as const,
  termine: (von?: string, bis?: string) => ["termine", von ?? "", bis ?? ""] as const,
  abgaben: (von?: string, bis?: string) => ["abgaben", von ?? "", bis ?? ""] as const,
  adminNutzer: ["admin", "nutzer"] as const,
  oeffentlichStand: ["oeffentlich", "stand"] as const,
  oeffentlichProjekt: (id: string) => ["oeffentlich", "projekt", id] as const,
  geocode: (q: string) => ["geocode", q] as const,
  /** §36: `GET /freigaben` — die Haken dazu stehen in `api/freigaben.ts`, der Schlüssel HIER,
   *  weil `useProjektNeu` ihn entwertet und `freigaben.ts` ohnehin aus dieser Datei liest
   *  (ein Schlüssel dort hieße: zwei Dateien, die sich gegenseitig einbinden). */
  freigaben: ["freigaben"] as const,
  /** §39: das Projekt-Cockpit (`api/cockpit.ts`: Kopf, Offen, Beschlüsse, Verlauf). Bewusst NICHT
   *  unter `["projekt", id]`: die Runde meldet sich anweisungsweise und OHNE Projektkennung (031),
   *  also braucht es einen Präfix, der jedes Cockpit trifft — und nur das Cockpit. Unter `projekt`
   *  hieße jeder Haken in einer laufenden Runde: jedes Projekt in jedem Zwischenspeicher neu holen.
   *  Der Schlüssel steht HIER und nicht in `cockpit.ts`, weil `entwerten` ihn mitnimmt (sonst zwei
   *  Dateien, die sich gegenseitig einbinden — wie bei `freigaben`). */
  cockpitAlle: ["cockpit"] as const,
  cockpit: (id: string) => ["cockpit", id] as const,
};

/* ========================================================== Anmeldung
   Keine Haken: die Anmeldung ist ein Formular mit eigenem Ablauf und keinem
   Zwischenspeicher. */

export const holeAuthConfig = () => api.get<AuthConfig>("/auth/config");

export const codeAnfordern = (email: string, turnstile: string | null) => api.post<AuthStart>("/auth/start", { email, turnstile });

export const codeEinloesen = (challenge: string, code: string, angemeldet_bleiben: boolean) =>
  api.post<AuthVerify>("/auth/verify", { challenge, code, angemeldet_bleiben });

export const abmelden = () => api.post<{ ok: boolean }>("/auth/logout");

export const holeMich = () => api.get<Ich>("/me");

/* ========================================================== Abfragen */

/**
 * `GET /konfig` — Kachelwirt, Stile, Gelände, ob es eine Ortssuche gibt.
 * Das ändert sich nur, wenn jemand die Umgebungsvariablen des Backends
 * anfasst und neu ausrollt; deshalb steht die Antwort quasi ewig
 * (`staleTime: Infinity`) und wird nie im Hintergrund nachgeholt. Sie ist
 * auch ohne Anmeldung zu haben — die öffentliche Karte braucht sie.
 */
export function useKonfig(): UseQueryResult<Konfig> {
  return useQuery({
    queryKey: schluessel.konfig,
    queryFn: () => api.get<Konfig>("/konfig"),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** `GET /me`. `aktiv` schaltet den Aufruf ab, solange niemand angemeldet ist. */
export function useIch(aktiv = true): UseQueryResult<Ich> {
  return useQuery({
    queryKey: schluessel.ich,
    queryFn: holeMich,
    enabled: aktiv,
    staleTime: 60_000,
  });
}

/**
 * `GET /stand` — die EINE Antwort für Karte, Board, Zeitleiste, Matrix und
 * Büroseiten.
 *
 * Wird beim Fensterwechsel und alle 60 Sekunden neu geholt (SPEC §7): in
 * diesem Haus schieben mehrere Leute gleichzeitig an denselben Projekten,
 * und ein Countdown, der eine Stunde alt ist, ist schlimmer als keiner.
 * `staleTime` liegt bewusst UNTER dem Intervall, sonst gäbe der Zwischen-
 * speicher die alte Antwort zurück und das Intervall liefe ins Leere.
 */
export function useStand(aktiv = true): UseQueryResult<Stand> {
  return useQuery({
    queryKey: schluessel.stand,
    queryFn: () => api.get<Stand>("/stand"),
    enabled: aktiv,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    // Im Hintergrundfenster NICHT weiterfragen: ein offener Reiter auf einem
    // Zweitschirm soll nachts nicht jede Minute das Backend wecken.
    refetchIntervalInBackground: false,
  });
}

/**
 * `GET /projekte/{id}` — der volle Satz samt Abgaben, Bildern und Grenze.
 *
 * Beim Fensterwechsel wird neu geholt, wie bei `useStand`: an einem offenen
 * Projektmodal sitzen im Haus mehrere Leute gleichzeitig, und wer von einem
 * anderen Reiter zurückkommt, soll nicht auf den nächsten Ereignisstoß warten
 * müssen (SPEC §11.2 — der Strom ist das Schnelle, dies hier das Sichere).
 */
export function useProjekt(id: string | null | undefined): UseQueryResult<Projekt> {
  return useQuery({
    queryKey: schluessel.projekt(id ?? ""),
    queryFn: () => api.get<Projekt>(`/projekte/${id}`),
    enabled: !!id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * `GET /abgaben?von=&bis=` — nur für die Zeitleiste mit gewähltem Fenster.
 * Ohne Fenster steht derselbe Satz schon in `Stand`; dann bitte den nehmen,
 * statt zweimal dasselbe zu holen.
 */
/**
 * `GET /projekte/{id}/stand` — die Leistungsphasen dieses Projekts mit Anteil,
 * Auftrag und den drei geltenden Ständen (§18.2).
 *
 * Eigene Abfrage und nicht ein Teil von `Projekt`: die Leiter interessiert nur
 * den Reiter „Stand", und sie in jede Akte zu legen hieße, sie in jedem
 * Kartenklick mitzuladen. Beträge stehen darin nur für die Geschäftsführung —
 * ob sie fehlen dürfen, sagt `lib/rechte.ts: darfGeldSehen`.
 */
export function useProjektStand(id: string | null | undefined, monat?: string): UseQueryResult<ProjektStand> {
  return useQuery({
    queryKey: schluessel.projektStand(id ?? "", monat),
    queryFn: () => api.get<ProjektStand>(`/projekte/${id}/stand` + frage({ monat })),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/**
 * `GET /staende?monat=` — die Leistungsstände ALLER Projekte für einen Monat (§23).
 *
 * Für die Listenansicht unter „Projekte". Gefiltert und gruppiert wird im
 * Browser: Namen, Büros und Leitungen stehen ohnehin schon in `/stand`, und ein
 * Filter, der jedes Mal den Server fragt, wäre beim Umschalten langsamer als
 * einer, der die geladene Liste durchgeht.
 */
export function useStaende(monat?: string, aktiv = true): UseQueryResult<StandListe> {
  return useQuery({
    queryKey: schluessel.staende(monat),
    queryFn: () => api.get<StandListe>("/staende" + frage({ monat })),
    enabled: aktiv,
    staleTime: 30_000,
  });
}

/**
 * Ein Wert in der LISTE — derselbe Weg wie `useMonatswert`, nur mit dem Projekt
 * im Rumpf des Aufrufs statt im Haken: die Liste trägt viele Projekte, und ein
 * Haken je Zeile wäre ein Haken in einer Schleife.
 */
export function useStandSetzen(monat: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projektId, phaseId, ...b }: MonatswertSetzen & { projektId: string; phaseId: string }) =>
      api.patch<ProjektStand>(`/projekte/${projektId}/stand/${monat}/${phaseId}`, b),
    onSuccess: (s) => {
      qc.setQueryData(schluessel.projektStand(s.projekt_id, monat), s);
      void qc.invalidateQueries({ queryKey: schluessel.projektVerlauf(s.projekt_id) });
      // Das Leitungsblatt schreibt Ziel und Abrechnung über denselben Weg und
      // rechnet daraus Schnitt und Ampeln — ohne `controllingEntwerten` stünden
      // sie still. Seit der Fehlerrunde 01.10.2026 (#17) der PRÄFIX `["staende"]`
      // statt nur dieses Monats: ein Wert in einem Monat ändert auch das
      // `vorher` des Folgemonats, falls der im Zwischenspeicher liegt.
      controllingEntwerten(qc);
      entwerten(qc, s.projekt_id);
    },
  });
}

/**
 * `GET /projekte/{id}/verlauf` — die dokumentierten Monate, jüngster zuerst (§20.3).
 *
 * Monate ohne Eintrag stehen NICHT darin: die Tabelle zeigt, was gemeldet wurde,
 * und nicht, wie viele Monate seither vergangen sind.
 */
export function useVerlauf(id: string | null | undefined, monate?: number): UseQueryResult<Verlauf> {
  return useQuery({
    queryKey: schluessel.projektVerlauf(id ?? ""),
    queryFn: () => api.get<Verlauf>(`/projekte/${id}/verlauf` + frage({ monate: monate ? String(monate) : undefined })),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/**
 * `GET /richtwerte` — alle Marken, einschließlich der stillgelegten.
 *
 * Die Verwaltung braucht sie vollständig; die Leiter eines Projekts bekommt die
 * aktiven schon fertig in `LphStand.richtwerte` mitgeliefert und muss diese
 * Abfrage gar nicht stellen.
 */
export function useRichtwerte(aktiv = true): UseQueryResult<Richtwert[]> {
  return useQuery({
    queryKey: schluessel.richtwerte,
    queryFn: () => api.get<Richtwert[]>("/richtwerte"),
    enabled: aktiv,
    staleTime: 300_000,
  });
}

/** `GET /termine` — über alle Projekte (§40.2). Ohne Fenster: ab dreißig Tagen zurück. */
export function useTermine(von?: string, bis?: string, aktiv = true): UseQueryResult<Termin[]> {
  return useQuery({
    queryKey: schluessel.termine(von, bis),
    queryFn: () => api.get<Termin[]>("/termine" + frage({ von, bis })),
    enabled: aktiv,
    staleTime: 30_000,
  });
}

/** Nach jeder Terminänderung: die Akte (sie trägt `termine`) und jede Terminliste. */
function termineEntwerten(qc: ReturnType<typeof useQueryClient>, projektId: string): void {
  void qc.invalidateQueries({ queryKey: schluessel.projekt(projektId) });
  void qc.invalidateQueries({ queryKey: schluessel.termineAlle });
}

export function useTerminNeu(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: TerminEingabe) => api.post<Termin>(`/projekte/${projektId}/termine`, b),
    onSuccess: () => termineEntwerten(qc, projektId),
  });
}

export function useTerminAendern(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: TerminAendern & { id: string }) => api.patch<Termin>(`/termine/${id}`, b),
    onSuccess: () => termineEntwerten(qc, projektId),
  });
}

export function useTerminLoeschen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/termine/${id}`),
    onSuccess: () => termineEntwerten(qc, projektId),
  });
}

export function useAbgaben(von?: string, bis?: string, aktiv = true): UseQueryResult<Abgabe[]> {
  return useQuery({
    queryKey: schluessel.abgaben(von, bis),
    queryFn: () => api.get<Abgabe[]>("/abgaben" + frage({ von, bis })),
    enabled: aktiv,
    staleTime: 30_000,
  });
}

/** `GET /admin/nutzer` — nur die Verwaltung ruft das. */
export function useAdminNutzer(aktiv = true): UseQueryResult<AdminNutzer[]> {
  return useQuery({
    queryKey: schluessel.adminNutzer,
    queryFn: () => api.get<AdminNutzer[]>("/admin/nutzer"),
    enabled: aktiv,
    staleTime: 30_000,
  });
}

/* ---- öffentlich (abgemeldet, E21) ---- */

export function useOeffentlichStand(aktiv = true): UseQueryResult<OeffentlichStand> {
  return useQuery({
    queryKey: schluessel.oeffentlichStand,
    queryFn: () => api.get<OeffentlichStand>("/oeffentlich/stand"),
    enabled: aktiv,
    // Das Backend setzt `Cache-Control: public, max-age=60`; ein kürzerer
    // Wert hier brächte nichts, weil der Browser ohnehin aus seinem eigenen
    // Zwischenspeicher antwortete.
    staleTime: 60_000,
  });
}

export function useOeffentlichProjekt(id: string | null | undefined): UseQueryResult<OeffentlichProjektDetail> {
  return useQuery({
    queryKey: schluessel.oeffentlichProjekt(id ?? ""),
    queryFn: () => api.get<OeffentlichProjektDetail>(`/oeffentlich/projekte/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/**
 * `GET /geocode?q=` — die Ortssuche beim Anlegen eines Projekts.
 * `aktiv` ist erst wahr, wenn wirklich etwas gesucht werden soll: der
 * Aufrufer entprellt die Eingabe (hooks/useEntprellt) und schaltet dann um.
 * `Konfig.ortssuche` sagt, ob es die Route überhaupt gibt (T12).
 */
export function useGeocode(q: string, aktiv: boolean, naehe?: { lon: number; lat: number }): UseQueryResult<Ortstreffer[]> {
  return useQuery({
    queryKey: [...schluessel.geocode(q), naehe?.lon ?? null, naehe?.lat ?? null],
    queryFn: () => api.get<Ortstreffer[]>("/geocode" + frage({ q, lon: naehe?.lon, lat: naehe?.lat })),
    enabled: aktiv && q.trim().length >= 2,
    staleTime: 5 * 60_000,
  });
}

/* ========================================================== Änderungen */

/**
 * Regel 2: nach JEDER Änderung Stand + Projekt entwerten.
 *
 * EXPORTIERT, weil nicht jede Änderung durch einen Haken laufen kann: das
 * Board (F4) verschiebt eine Karte in eine andere Phase, und die Kennung des
 * Projekts steht erst im Ereignisbehandler fest — `useProjektAendern(id)`
 * bindet sie aber schon beim Aufruf des Hakens, und Haken lassen sich nicht
 * je Karte rufen. Wer von Hand patcht, ruft danach `entwerten(qc, id)` und
 * hat damit GENAU die Schlüssel erwischt, die auch die Haken entwerten —
 * eine abgeschriebene Liste in einem zweiten Bildschirm läuft sonst
 * auseinander, sobald hier ein Schlüssel dazukommt.
 *
 * Was `entwerten` NICHT mitbringt, ist die 409-Behandlung aus `mitKonflikt`.
 * Wer von Hand patcht, holt sich die frische `version` unmittelbar davor und
 * nimmt einen Fehlschlag als Rückrollen der Karte — so macht es das Board.
 */
export function entwerten(qc: QueryClient, projektId?: string | null): void {
  void qc.invalidateQueries({ queryKey: schluessel.stand });
  // Der PRÄFIX-Schlüssel, nicht `abgaben("", "")`: die Zeitleiste legt ihre
  // Antworten unter ["abgaben", von, bis] ab, und ein Schlüssel mit zwei
  // leeren Feldern trifft davon genau einen.
  void qc.invalidateQueries({ queryKey: schluessel.abgabenAlle });
  if (projektId) {
    void qc.invalidateQueries({ queryKey: schluessel.projekt(projektId) });
    // §39: das Cockpit liegt NICHT unter `["projekt", id]` (siehe `schluessel.cockpit`), liest
    // aber Ziel, Abgaben, Termine, Bilder und Stand desselben Projekts — wer hier ändert, sieht
    // die Jetzt-Leiste sonst erst nach dem Ereignisstrom (500 ms) oder gar nicht, wenn er hängt.
    void qc.invalidateQueries({ queryKey: schluessel.cockpit(projektId) });
  }
}

/**
 * Die zwei Controllingansichten, die NICHT unter `["projekt", id]` liegen: die
 * Standliste aller Projekte (§23, `["staende", monat]`) und das Blatt der
 * Geschäftsführung (§18.6, `["leitung"]`). Fehlerrunde 01.10.2026, #17: nur
 * `useStandSetzen` entwertete sie, und das nur für EINEN Monat — wer im Reiter
 * „Stand" der Akte meldete, sah Liste und Blatt erst nach einem Neuaufbau neu.
 * Der Präfix `["staende"]` trifft jeden Monat im Zwischenspeicher.
 */
export function controllingEntwerten(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: schluessel.staendeAlle });
  void qc.invalidateQueries({ queryKey: schluessel.leitung });
}

/** Sieht dieser Körper aus wie ein `Projekt`? Genug geprüft, um ihn abzulegen. */
function istProjekt(body: unknown): body is Projekt {
  return !!body && typeof body === "object" && typeof (body as { id?: unknown }).id === "string" && typeof (body as { version?: unknown }).version === "number";
}

/**
 * Den 409-Körper zu einem sauberen `Projekt` machen.
 *
 * Der Körper eines 409 ist ein SUPERSATZ: der aktuelle `Projekt` PLUS eine
 * deutsche `detail`-Zeile (so einigten sich B2 und B3 in docs/INTEGRATION.md,
 * und SPEC §2 verlangt beides — „409 … body: the current Projekt" und
 * „Error bodies are {detail: …}"). Beides zusammen ist richtig; nur gehören
 * die zwei Hälften an verschiedene Orte:
 *
 *   `detail`  → in den `ApiFehler`, damit der Toast einen Satz hat. Das tut
 *               `client.ts` bereits von selbst (`meldungAus` liest `detail`).
 *   der Rest  → in den Zwischenspeicher als `['projekt', id]`.
 *
 * Deshalb wird `detail` hier ABGESCHNITTEN, bevor der Stand abgelegt wird.
 * Ohne das trüge jeder Eintrag, der einmal über einen Konflikt gelaufen ist,
 * einen Schlüssel, den `Projekt` nicht kennt — und der wanderte beim nächsten
 * „Speichern" als Feld in den PATCH-Körper zurück.
 *
 * Ausgeführt und nicht eingebaut, damit auch derjenige es bekommt, der von
 * Hand patcht (F4s Board tut das, weil es die Projektkennung erst im
 * Ereignisbehandler kennt): `qc.setQueryData(schluessel.projekt(id),
 * ohneDetail(koerper))`.
 */
export function ohneDetail(body: Projekt): Projekt {
  if (!("detail" in body)) return body;
  const { detail: _detail, ...rest } = body as Projekt & { detail?: unknown };
  return rest;
}

/**
 * Regel 3: ein 409 legt den mitgelieferten Projektstand ab und wirft weiter.
 * Jede Änderung an einem Projekt läuft hier durch.
 *
 * Die `version` wird dabei NIE stillschweigend übernommen und der Aufruf nicht
 * wiederholt — das wäre die optimistische Sperre ausgehebelt. Der Bildschirm
 * zeigt beim Weiterwerfen schon den neuen Stand, und die Meldung „jemand
 * anderes war schneller" steht vor Daten, die stimmen.
 */
async function mitKonflikt<T>(qc: QueryClient, projektId: string, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (istApiFehler(e) && e.status === 409 && istProjekt(e.body)) {
      qc.setQueryData(schluessel.projekt(projektId), ohneDetail(e.body));
      void qc.invalidateQueries({ queryKey: schluessel.stand });
    }
    // 403: das Schreibrecht ist unterwegs erloschen — etwa mit der Freigabe (§36.5,
    // die anlegende Person schreibt danach nur noch im kleinen Recht). Der 403 trägt
    // keinen `Projekt`-Körper wie der 409, also den Stand neu holen: sonst stünde
    // der Stift weiter da und jeder weitere Versuch liefe in denselben Fehler.
    if (istApiFehler(e) && e.status === 403) {
      void qc.invalidateQueries({ queryKey: schluessel.projekt(projektId) });
    }
    throw e;
  }
}

/* ---- Projekt ---- */

export function useProjektNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: ProjektNeu) => api.post<Projekt>("/projekte", b),
    onSuccess: (p) => {
      // Der neue Satz ist schon vollständig da — ihn abzulegen spart dem
      // Bildschirm, der gleich dorthin navigiert, den ersten Ladebalken.
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
      // §36: ein Projekt der Mitarbeit kommt `offen` zurück und taucht in der
      // Karte „Freigaben" der anlegenden Person auf — auch ohne Ereignisstrom.
      void qc.invalidateQueries({ queryKey: schluessel.freigaben });
      // Die Feier (§16.3): der Server legt zu jedem neuen Projekt einen
      // Erfolgsbeitrag an. Sie steht HIER und nicht an jedem Aufrufer —
      // „Projekt anlegen" gibt es an drei Stellen (Karte, Galerie, Board),
      // und drei Stellen sind zwei Gelegenheiten, es zu vergessen.
      // Seit §36 nur, wenn es gleich freigegeben ist: sonst gibt es noch keinen
      // Beitrag, und gefeiert wird bei der Freigabe (`useProjektFreigeben`).
      if (p.freigabe === "freigegeben") {
        useUi.getState().feiern({ art: "projekt", titel: p.name, untertitel: p.ort });
        void qc.invalidateQueries({ queryKey: aktuellesSchluessel.alle });
      }
    },
  });
}

export function useProjektAendern(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: ProjektAendern) => mitKonflikt(qc, projektId, () => api.patch<Projekt>(`/projekte/${projektId}`, b)),
    onSuccess: (p) => {
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
    },
  });
}

/**
 * `PATCH /projekte/{id}/team/ich` — die eigene Abgaben-Erinnerung an- und
 * abschalten (SPEC §11.4).
 *
 * Eine EIGENE Route und nicht `PATCH /projekte/{id}` mit `team`: das Team zu
 * schreiben verlangt Bearbeitungsrecht und ersetzt den ganzen Satz. Hier dreht
 * jedes Teammitglied genau EINEN Wert an SEINEM Eintrag — auch ohne
 * Bearbeitungsrecht. Deshalb traegt der Aufruf auch keine `version`: er kann
 * mit keiner fremden Aenderung kollidieren, und ein 409 waere hier nur eine
 * Huerde ohne Gewinn.
 */
export function useTeamIch(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (benachrichtigen: boolean) =>
      api.patch<Projekt>(`/projekte/${projektId}/team/ich`, { benachrichtigen }),
    onSuccess: (p) => {
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
    },
  });
}

/**
 * `PUT /projekte/{id}/team/ich` — sich selbst ins Team eintragen oder wieder
 * austragen (SPEC §22.3, §40.4). Direkt, ohne Anfrage; die Projektleitung bekommt
 * eine Mail. Ohne `version`, aus demselben Grund wie die Glocke darüber.
 */
export function useSelbstEintragen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dabei: boolean) => api.put<Projekt>(`/projekte/${projektId}/team/ich`, { dabei }),
    onSuccess: (p) => {
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
    },
  });
}

export function useProjektLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projektId: string) => api.del<void>(`/projekte/${projektId}`),
    onSuccess: (_d, projektId) => {
      // Die Zeile ist fort — der abgelegte Satz muss weg, sonst zeigt ein
      // direkter Aufruf der Adresse das gelöschte Projekt aus dem
      // Zwischenspeicher, bevor der 404 eintrifft.
      qc.removeQueries({ queryKey: schluessel.projekt(projektId) });
      entwerten(qc, null);
      // §36: wer eine abgelehnte Einreichung löscht, räumt damit auch den Punkt
      // an „Start" und die Zeile in der Karte „Freigaben" ab — sofort, wie
      // `useProjektNeu`, und nicht erst, wenn der Ereignisstrom meldet.
      void qc.invalidateQueries({ queryKey: schluessel.freigaben });
    },
  });
}

/* ---- Grenze (E13, E26) ---- */

export function useGrenzeSetzen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: GrenzeSetzen) => mitKonflikt(qc, projektId, () => api.put<Projekt>(`/projekte/${projektId}/grenze`, b)),
    onSuccess: (p) => {
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
    },
  });
}

/**
 * Datei(en) hineingeben und die VORSCHAU bekommen. Es wird noch nichts
 * gespeichert (N3): die Antwort trägt das erkannte Koordinatensystem und die
 * gefundenen Flächen, und erst `useGrenzUebernehmen` schreibt.
 * Deshalb entwertet dieser Haken auch nichts.
 */
export function useGrenzImport(projektId: string) {
  return useMutation({
    mutationFn: (dateien: File[]) => {
      const fd = new FormData();
      for (const d of dateien) fd.append("dateien", d);
      return api.upload<GrenzImport>(`/projekte/${projektId}/grenze/import`, fd);
    },
  });
}

/**
 * Dieselbe Vorschau, aber OHNE Projekt (SPEC §17): beim Anlegen wird die
 * Grenze gleich mit hochgeladen. `naehe` ordnet nur die Kandidaten, wenn die
 * Datei kein Koordinatensystem nennt.
 */
export function useGrenzImportFrei() {
  return useMutation({
    mutationFn: ({ dateien, naehe }: { dateien: File[]; naehe?: LonLat | null }) => {
      const fd = new FormData();
      for (const d of dateien) fd.append("dateien", d);
      if (naehe) {
        fd.append("lon", String(naehe[0]));
        fd.append("lat", String(naehe[1]));
      }
      return api.upload<GrenzImport>("/grenz-importe", fd);
    },
  });
}

/** Ein anderes Koordinatensystem wählen — die Datei wird damit neu gelesen. */
export function useGrenzKbs(importId: string) {
  return useMutation({
    mutationFn: (epsg: number) => api.post<GrenzImport>(`/grenz-importe/${importId}/kbs`, { epsg }),
  });
}

/**
 * §33: die Ebene einer DWG/DXF-Zeichnung wählen, auf der die Grenze liegt. Die
 * Zeichnung wird damit neu umgewandelt, und erst jetzt schätzt der Server die
 * Lage — für genau diese Ebene.
 */
export function useGrenzEbene(importId: string) {
  return useMutation({
    mutationFn: (ebene: string) =>
      api.post<GrenzImport>(`/grenz-importe/${importId}/ebene`, { ebene } satisfies GrenzEbeneWaehlen),
  });
}

/** Die gewählten Flächen vereinigen und als Projektgrenze schreiben. */
export function useGrenzUebernehmen(importId: string, projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: GrenzUebernehmen) =>
      mitKonflikt(qc, projektId, () => api.post<Projekt>(`/grenz-importe/${importId}/uebernehmen`, b)),
    onSuccess: (p) => {
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
    },
  });
}

/* ---- Konzeptbestandteile (007, SPEC §12.1) ----
   Drei Routen, alle mit dem ganzen `Projekt` als Antwort — anders als bei den
   Abgaben, die ihre eigene Zeile zurueckgeben. Das ist die Vorgabe des
   Vertrags und hier auch die bequemere Haelfte: `konzept_fortschritt` steht in
   `ProjektKurz` und aendert sich bei jedem Haken mit, und eine Antwort, die
   nur den einen Bestandteil traegt, liesse den Balken daneben veralten.

   Kein `mitKonflikt`: ein Bestandteil traegt keine `version`, und zwei Leute,
   die gleichzeitig zwei verschiedene Haken setzen, sind kein Konflikt — beide
   Haken sollen stehen. */

export function useKonzeptNeu(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: KonzeptTeilNeu) => api.post<Projekt>(`/projekte/${projektId}/konzept`, b),
    onSuccess: (p) => {
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
    },
  });
}

export function useKonzeptAendern(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: KonzeptTeilAendern & { id: string }) =>
      api.patch<Projekt>(`/projekte/${projektId}/konzept/${id}`, b),
    onSuccess: (p) => {
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
    },
  });
}

export function useKonzeptLoeschen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<Projekt>(`/projekte/${projektId}/konzept/${id}`),
    onSuccess: (p) => {
      qc.setQueryData(schluessel.projekt(p.id), p);
      entwerten(qc, p.id);
    },
  });
}

/* ---- Abgaben (E11) ---- */

export function useAbgabeNeu(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: AbgabeEingabe) => api.post<Abgabe>(`/projekte/${projektId}/abgaben`, b),
    onSuccess: () => entwerten(qc, projektId),
  });
}

export function useAbgabeAendern(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: Partial<AbgabeEingabe> & { id: string }) => api.patch<Abgabe>(`/abgaben/${id}`, b),
    onSuccess: (a) => {
      entwerten(qc, projektId);
      // §16.3: wechselt der Status auf `abgegeben`, legt der Server einen
      // Erfolgsbeitrag an und hängt seine Kennung als ZUSÄTZLICHEN Schlüssel
      // an die Antwort. `Abgabe` kennt das Feld laut Vertrag NICHT — deshalb
      // wird es lose gelesen und nicht in den Typ geschrieben.
      const erfolg = (a as unknown as { erfolg_beitrag_id?: string | null }).erfolg_beitrag_id;
      if (!erfolg) return;
      useUi.getState().feiern({ art: "abgabe", titel: a.titel });
      void qc.invalidateQueries({ queryKey: aktuellesSchluessel.alle });
    },
  });
}

export function useAbgabeLoeschen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/abgaben/${id}`),
    onSuccess: () => entwerten(qc, projektId),
  });
}

/* ---- Galerie (E14) ---- */

/**
 * `POST /projekte/{id}/bilder` — ein Stapel Dateien.
 *
 * Die Projektkennung reist als VARIABLE mit und steckt nicht im Haken
 * (Fehlerrunde 01.10.2026, #5): `mutateAsync` liest Weg und `onSuccess` aus
 * den Optionen, die der Beobachter GERADE trägt — und die setzt jedes
 * Zeichnen neu. Das Projektmodal bleibt beim Blättern zum Nachbarn stehen;
 * Stapel 2 und 3 einer Auswahl aus Projekt A landeten so per
 * `/projekte/B/bilder` im Nachbarprojekt. Mit der Kennung im Aufruf gilt, was
 * beim Start des Laufs feststand.
 */
export function useBilderHochladen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projektId, dateien, phase_id }: { projektId: string; dateien: File[]; phase_id?: string | null }) => {
      const fd = new FormData();
      for (const d of dateien) fd.append("dateien", d);
      if (phase_id) fd.append("phase_id", phase_id);
      return api.upload<Bild[]>(`/projekte/${projektId}/bilder`, fd);
    },
    // Die Antwort kommt sofort, die Ableitungen entstehen im Hintergrund
    // (SPEC §6, `status: "wartet"`). Der Bildschirm fragt das Projekt danach
    // in Abständen neu ab — deshalb hier nur entwerten und nichts ablegen.
    // Auch nach einem FEHLER (Fehlerrunde 01.10.2026, #12): bricht die Leitung
    // ab, nachdem der Server schon festgeschrieben hat, stehen die Bilder da,
    // obwohl der Bildschirm einen Fehler meldet.
    onSettled: (_bilder, _fehler, v) => entwerten(qc, v.projektId),
  });
}

export function useBildAendern(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: BildAendern & { id: string }) => api.patch<Bild>(`/bilder/${id}`, b),
    onSuccess: () => entwerten(qc, projektId),
  });
}

export function useBildLoeschen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/bilder/${id}`),
    onSuccess: () => entwerten(qc, projektId),
  });
}

/* ---- eigenes Profil (E15) ---- */

export function useIchAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: IchAendern) => api.patch<Ich>("/me", b),
    onSuccess: (ich) => {
      qc.setQueryData(schluessel.ich, ich);
      // Der eigene Name, das Kürzel und das Heimatbüro stehen auch in
      // `Stand.personen` — die Karte zeichnet den Avatar daraus.
      void qc.invalidateQueries({ queryKey: schluessel.stand });
    },
  });
}

/**
 * `DELETE /me/steckbrief` — der Widerruf mit einem Klick (SPEC §10.1).
 *
 * Er leert alle sieben Steckbrieffelder in EINER Antwort und nicht als sieben
 * leere Werte über `PATCH /me`: zur Freiwilligkeit gehört, dass die Zustimmung
 * jederzeit und ohne Begründung zurückgenommen werden kann — und eine
 * Rücknahme, die aus sieben Teilschritten besteht, kann auf halbem Weg
 * steckenbleiben (Art. 6 Abs. 1 lit. a DSGVO, docs/RECHERCHE-STECKBRIEF.md).
 */
export function useSteckbriefLeeren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<Ich>("/me/steckbrief"),
    onSuccess: (ich) => {
      qc.setQueryData(schluessel.ich, ich);
      // Die Steckbriefworte stehen auch in `Stand.personen` — Karte und
      // Teamseite zeichnen daraus.
      void qc.invalidateQueries({ queryKey: schluessel.stand });
    },
  });
}

export function useAvatarHochladen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datei: Blob) => {
      const fd = new FormData();
      // Ein Blob ohne Namen landet serverseitig als "blob" — der Dateiname
      // entscheidet dort über gar nichts (der Typ kommt aus den Bytes), aber
      // in einem Fehlerprotokoll liest sich „avatar.webp" besser.
      fd.append("datei", datei, "avatar.webp");
      return api.upload<Ich>("/me/avatar", fd);
    },
    onSuccess: (ich) => {
      qc.setQueryData(schluessel.ich, ich);
      void qc.invalidateQueries({ queryKey: schluessel.stand });
    },
  });
}

export function useAvatarLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<Ich>("/me/avatar"),
    onSuccess: (ich) => {
      qc.setQueryData(schluessel.ich, ich);
      void qc.invalidateQueries({ queryKey: schluessel.stand });
    },
  });
}

/* ---- Verwaltung: Personen ---- */

export function useNutzerNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: AdminNutzerNeu) => api.post<AdminNutzer>("/admin/nutzer", b),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schluessel.adminNutzer });
      void qc.invalidateQueries({ queryKey: schluessel.stand });
    },
  });
}

export function useNutzerAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: AdminNutzerAendern & { id: string }) => api.patch<AdminNutzer>(`/admin/nutzer/${id}`, b),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schluessel.adminNutzer });
      void qc.invalidateQueries({ queryKey: schluessel.stand });
    },
  });
}

export function useEntsperren() {
  return useMutation({
    mutationFn: (email: string) => api.post<{ entsperrt: number }>("/admin/entsperren", { email }),
  });
}

/* ---- Verwaltung: Büros und Phasen ----
   Beide stehen in `Stand`; es gibt keine eigene Leseroute und deshalb auch
   keinen eigenen Schlüssel. Nach einer Änderung wird `['stand']` entwertet,
   und die Verwaltung liest denselben Satz wie die Karte. */

export function useBueroNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: BueroEingabe) => api.post<Buero>("/bueros", b),
    onSuccess: () => entwerten(qc, null),
  });
}

export function useBueroAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: Partial<BueroEingabe> & { id: string }) => api.patch<Buero>(`/bueros/${id}`, b),
    onSuccess: () => entwerten(qc, null),
  });
}

export function useBueroLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/bueros/${id}`),
    onSuccess: () => entwerten(qc, null),
  });
}

/**
 * `PUT /projekte/{id}/lph` — das Auftragsbuch ersetzen (§18.2).
 *
 * Die Antwort ist der frische `ProjektStand` und wird gleich abgelegt: der
 * Reiter zeigt danach ohne zweiten Gang, was der Server behalten hat
 * (einschließlich der Beträge, die er selbst gerechnet hat). `entwerten`
 * daneben, weil `meilenstein`, Phasenfarbe und Kopf der Akte am Projekt hängen.
 *
 * Kein `mitKonflikt`: die Leiter hat keine `version` (SPEC §18.4) — die Sperre
 * sitzt am Projektkopf, an dem die halbe Firma vorbeikommt, nicht hier.
 */
/**
 * `PATCH /projekte/{id}/stand/{monat}/{phase_id}` — ein Feld, ein Aufruf (§20.3).
 *
 * Die Antwort ist die ganze Leiter dieses Monats und wird gleich abgelegt; der
 * Reiter zeigt danach ohne zweiten Gang, was der Server behalten hat. `entwerten`
 * daneben, weil der Kopf der Akte (Phase, Meilenstein) am Projekt hängt.
 *
 * Der Monat steckt im Haken und nicht im Rumpf: er gehört zum Weg, und ein Haken je
 * Monat hält den Zwischenspeicher je Monat getrennt.
 */
export function useMonatswert(projektId: string, monat: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, ...b }: MonatswertSetzen & { phaseId: string }) =>
      api.patch<ProjektStand>(`/projekte/${projektId}/stand/${monat}/${phaseId}`, b),
    onSuccess: (s) => {
      qc.setQueryData(schluessel.projektStand(projektId, monat), s);
      void qc.invalidateQueries({ queryKey: schluessel.projektVerlauf(projektId) });
      // Fehlerrunde 01.10.2026, #17: Standliste und Leitungsblatt rechnen aus
      // demselben Wert — ohne diese Zeile zeigte die Liste nach dem Zurückgehen
      // bis zu 30 s lang die alte Zahl (`staleTime`), das Blatt bis zum Neuaufbau.
      controllingEntwerten(qc);
      entwerten(qc, projektId);
    },
  });
}

export function useLphSetzen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: ProjektLphSetzen[]) => api.put<ProjektStand>(`/projekte/${projektId}/lph`, b),
    onSuccess: (s) => {
      // Die Antwort gilt für den LAUFENDEN Monat (so antwortet die Route); ein
      // anderer Monat im Zwischenspeicher wird entwertet statt überschrieben.
      qc.setQueryData(schluessel.projektStand(projektId, s.monat.slice(0, 7)), s);
      // Fehlerrunde 01.10.2026, #17: Beauftragung und Anteile tragen die
      // Gewichte der Standliste und des Leitungsblatts — beide mit entwerten.
      controllingEntwerten(qc);
      entwerten(qc, projektId);
    },
  });
}

/* ---- Richtwerte (Verwaltung, §18.2) ----
   Eine geänderte Marke wirkt in JEDER Projektleiter — darum wird der ganze
   Präfix `["projekt"]` entwertet und nicht ein einzelnes Projekt. Das ist
   grob, aber richtig: Marken ändert das Haus selten, und die Alternative wäre
   eine Liste der Projekte, die diese Phase führen. */

function richtwerteEntwerten(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: schluessel.richtwerte });
  void qc.invalidateQueries({ queryKey: ["projekt"] });
}

export function useRichtwertNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: RichtwertEingabe) => api.post<Richtwert>("/richtwerte", b),
    onSuccess: () => richtwerteEntwerten(qc),
  });
}

export function useRichtwertAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: RichtwertEingabe & { id: string }) => api.patch<Richtwert>(`/richtwerte/${id}`, b),
    onSuccess: () => richtwerteEntwerten(qc),
  });
}

export function useRichtwertLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/richtwerte/${id}`),
    onSuccess: () => richtwerteEntwerten(qc),
  });
}

/**
 * `GET /leitung/uebersicht` — das Blatt der Geschäftsführung (§18.6).
 *
 * `aktiv` ist hier kein Luxus: die Route antwortet allen anderen mit 403, und ein
 * Haken, der beim Laden jeder Seite einmal dagegenläuft, füllt das Protokoll mit
 * Fehlern, die keine sind. Der Bildschirm ruft ihn nur, wenn die Rolle passt.
 */
export function useLeitung(aktiv = true): UseQueryResult<LeitungUebersicht> {
  return useQuery({
    queryKey: schluessel.leitung,
    queryFn: () => api.get<LeitungUebersicht>("/leitung/uebersicht"),
    enabled: aktiv,
    staleTime: 30_000,
  });
}

/* ---- Funktionswünsche (§21, 017) ----
   Jede Änderung entwertet die ganze Liste und legt nicht die eine Zeile ab: die
   REIHENFOLGE kommt aus der Datenbank (Offenes zuerst, darin nach Punkten), und
   eine Stimme kann einen Wunsch nach oben schieben. Ein Client, der nur die
   geänderte Zeile ersetzt, zeigte die neue Zahl an der alten Stelle. */

export function useWuensche(aktiv = true): UseQueryResult<Wunsch[]> {
  return useQuery({
    queryKey: schluessel.wuensche,
    queryFn: () => api.get<Wunsch[]>("/wuensche"),
    enabled: aktiv,
    staleTime: 30_000,
  });
}

export function useWunschNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: WunschNeu) => api.post<Wunsch>("/wuensche", b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: schluessel.wuensche }),
  });
}

export function useWunschStimme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, wert }: { id: string; wert: -1 | 0 | 1 }) => api.put<Wunsch>(`/wuensche/${id}/stimme`, { wert }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: schluessel.wuensche }),
  });
}

export function useWunschAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: WunschAendern & { id: string }) => api.patch<Wunsch>(`/wuensche/${id}`, b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: schluessel.wuensche }),
  });
}

export function useWunschLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/wuensche/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: schluessel.wuensche }),
  });
}

export function usePhaseNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: PhaseEingabe) => api.post<Phase>("/phasen", b),
    onSuccess: () => entwerten(qc, null),
  });
}

export function usePhaseAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: Partial<PhaseEingabe> & { id: string }) => api.patch<Phase>(`/phasen/${id}`, b),
    onSuccess: () => entwerten(qc, null),
  });
}

export function usePhaseLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/phasen/${id}`),
    onSuccess: () => entwerten(qc, null),
  });
}

/* ==================================================== Aktuelles (§16, 011)
   Feed, Forum, Erfolge. Drei Dinge, die hier anders laufen als oben:

   1. DER FEED IST EINE `useInfiniteQuery`. `GET /aktuelles` gibt 30 Beiträge
      und einen undurchsichtigen Zeiger `weiter`; die Seiten bleiben
      ANEINANDER, statt bei jedem Nachladen neu zu holen. Der Schlüssel trägt
      den Filter, damit ein Bereichswechsel eine eigene Liste ist und nicht
      die alte fortschreibt.
   2. DIE SCHLÜSSEL LIEGEN GETRENNT: `["aktuelles","liste",…]` für die Seiten,
      `["aktuelles","beitrag",id]` für den einzelnen Satz. Der Ereignisstrom
      (lib/ereignisse.ts) entwertet das PRÄFIX `["aktuelles"]` und trifft
      damit beides; die Änderungshaken schreiben den zurückgegebenen Beitrag
      in den Einzelschlüssel und entwerten nur die Listen — sonst würfen sie
      den Satz weg, den sie gerade abgelegt haben.
   3. REAKTIONEN GEHEN OPTIMISTISCH. Ein Applaus ist eine Handlung mit einer
      Erwartung von „sofort"; auf die Antwort zu warten, macht aus dem Knopf
      einen Ladebalken. Der Zwischenspeicher wird deshalb vorher gedreht, bei
      einem Fehler zurückgerollt und bei Erfolg auf die Serverzahlen gesetzt —
      die sind die Wahrheit, denn nebenan klatscht jemand mit. */

export interface AktuellesFilter {
  art?: BeitragArt | null;
  bereich?: Bereich | null;
  projekt_id?: string | null;
  /** nur Beiträge mit `erfolg_art` */
  erfolg?: boolean;
  /** trifft Titel, Text und Autorname */
  suche?: string;
}

/** Der Filterteil des Abfrageschlüssels — ausgeschrieben, damit er stabil ist. */
function filterSchluessel(f: AktuellesFilter): string[] {
  return [f.art ?? "", f.bereich ?? "", f.projekt_id ?? "", f.erfolg ? "1" : "", (f.suche ?? "").trim()];
}

export const aktuellesSchluessel = {
  /** PRÄFIX über Listen UND Einzelsätze — für den Ereignisstrom. */
  alle: ["aktuelles"] as const,
  /** PRÄFIX über alle Filterfassungen der Liste. */
  listen: ["aktuelles", "liste"] as const,
  liste: (f: AktuellesFilter) => ["aktuelles", "liste", ...filterSchluessel(f)] as const,
  beitrag: (id: string) => ["aktuelles", "beitrag", id] as const,
  /** Die Bereichsliste (§16.6). Sie liegt UNTER `["aktuelles"]` und daneben,
   *  nicht unter `["aktuelles","liste"]`: der Ereignisstrom trifft sie damit
   *  über dasselbe Präfix, und die Änderungshaken der Beiträge, die nur
   *  `listen` entwerten, lassen sie in Ruhe. */
  bereiche: ["aktuelles", "bereiche"] as const,
  oeffentlich: ["oeffentlich", "aktuelles"] as const,
};

/**
 * `GET /aktuelles/bereiche` — die Hausbereiche des Forums, MIT den stillgelegten.
 *
 * Die stillgelegten kommen mit, weil die Oberfläche sie braucht: ein Beitrag in
 * einem stillgelegten Bereich soll seine Plakette weiter mit dem Namen tragen und
 * nicht mit dem nackten Schlüssel. Wer in einer Auswahl erscheint, entscheidet
 * `aktiv` an der jeweiligen Stelle — nicht diese Abfrage.
 *
 * `staleTime` großzügig: Stammdaten ändern sich selten, und wenn doch, meldet es
 * der Ereignisstrom (`lib/ereignisse.ts`, Tabelle `bereich`).
 */
export function useBereiche(aktiv = true): UseQueryResult<BereichEintrag[]> {
  return useQuery({
    queryKey: aktuellesSchluessel.bereiche,
    queryFn: () => api.get<BereichEintrag[]>("/aktuelles/bereiche"),
    enabled: aktiv,
    staleTime: 5 * 60_000,
  });
}

/* ---- Verwaltung: Bereiche ----
   Anders als Büros und Phasen stehen sie NICHT in `Stand` (§16.6) — entwertet
   wird deshalb ihr eigener Schlüssel und sonst nichts. Die Beiträge tragen den
   SCHLÜSSEL, und der ändert sich nie; eine geladene Feedseite bleibt nach einem
   Umbenennen richtig, nur die Beschriftung daneben ist neu.

   Der Ereignisstrom greift gröber (`lib/ereignisse.ts` entwertet auf die Tabelle
   `bereich` hin das ganze Präfix `["aktuelles"]`), und das ist dort auch richtig:
   er weiß nicht, WAS sich geändert hat, und ein gelöschter Bereich nimmt sehr wohl
   Beiträge aus der Sicht. Hier weiß der Aufrufer es. */

function bereicheEntwerten(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: aktuellesSchluessel.bereiche });
}

export function useBereichNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: BereichEingabe) => api.post<BereichEintrag>("/bereiche", b),
    onSuccess: () => bereicheEntwerten(qc),
  });
}

export function useBereichAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ schluessel, ...b }: Partial<BereichEingabe> & { schluessel: string }) =>
      api.patch<BereichEintrag>(`/bereiche/${encodeURIComponent(schluessel)}`, b),
    onSuccess: () => bereicheEntwerten(qc),
  });
}

export function useBereichLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schluessel: string) => api.del<void>(`/bereiche/${encodeURIComponent(schluessel)}`),
    onSuccess: () => bereicheEntwerten(qc),
  });
}

/**
 * `GET /aktuelles` — der Feed bzw. das Forum, seitenweise.
 *
 * `refetchOnWindowFocus` wie bei `useStand`: wer von einem anderen Reiter
 * zurückkommt, soll nicht auf den nächsten Ereignisstoß warten. Nachgeladene
 * Seiten bleiben dabei stehen (TanStack holt alle geladenen Seiten neu).
 */
export function useAktuelles(filter: AktuellesFilter = {}, aktiv = true): UseInfiniteQueryResult<{ pages: AktuellesSeite[]; pageParams: unknown[] }> {
  return useInfiniteQuery({
    queryKey: aktuellesSchluessel.liste(filter),
    queryFn: ({ pageParam }) =>
      api.get<AktuellesSeite>(
        "/aktuelles" +
          frage({
            art: filter.art,
            bereich: filter.bereich,
            projekt_id: filter.projekt_id,
            erfolg: filter.erfolg ? 1 : undefined,
            suche: filter.suche?.trim(),
            weiter: pageParam,
          }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (letzte: AktuellesSeite) => letzte.weiter,
    enabled: aktiv,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** `GET /aktuelles/{id}` — der volle Beitrag samt Kommentaren. */
export function useBeitrag(id: string | null | undefined): UseQueryResult<Beitrag> {
  return useQuery({
    queryKey: aktuellesSchluessel.beitrag(id ?? ""),
    queryFn: () => api.get<Beitrag>(`/aktuelles/${id}`),
    enabled: !!id,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/** `GET /oeffentlich/aktuelles` — die freigegebenen Beiträge fürs Schaufenster. */
export function useOeffentlichAktuelles(aktiv = true): UseQueryResult<OeffentlichBeitrag[]> {
  return useQuery({
    queryKey: aktuellesSchluessel.oeffentlich,
    queryFn: () => api.get<OeffentlichBeitrag[]>("/oeffentlich/aktuelles"),
    enabled: aktiv,
    staleTime: 60_000,
  });
}

/**
 * Nur die ERSTE Seite, für den Ungelesen-Punkt am Reiter.
 *
 * Eine eigene Abfrage und kein Griff in die Liste: der Punkt steht in der
 * Kopfleiste, also auf JEDEM Bildschirm, und die Liste gibt es nur, solange
 * „Aktuelles" offen ist. `Ich.aktuelles_ungelesen` kommt laut Vertrag
 * ausdrücklich NICHT (SPEC §16.2) — der Client zählt selbst.
 */
export function useNeuesteBeitraege(aktiv = true): UseQueryResult<AktuellesSeite> {
  return useQuery({
    queryKey: ["aktuelles", "neueste"] as const,
    queryFn: () => api.get<AktuellesSeite>("/aktuelles"),
    enabled: aktiv,
    staleTime: 60_000,
    refetchInterval: 120_000,
    // Nachts auf einem Zweitschirm soll niemand das Backend wecken.
    refetchIntervalInBackground: false,
  });
}

/* ---- Beiträge ---- */

/** Den zurückgegebenen Beitrag ablegen und NUR die Listen entwerten.
 *  §53: exportiert für `api/videos.ts` (Video-Upload mit Fortschritt). */
export function legeBeitragAb(qc: QueryClient, b: Beitrag): void {
  qc.setQueryData(aktuellesSchluessel.beitrag(b.id), b);
  void qc.invalidateQueries({ queryKey: aktuellesSchluessel.listen });
  void qc.invalidateQueries({ queryKey: ["aktuelles", "neueste"] });
  // §37.10: die Sprechblasen der Karte (`beitragOrteSchluessel` in
  // `api/ansichten.ts` — hier ausgeschrieben, weil jene Datei diese einbindet).
  void qc.invalidateQueries({ queryKey: ["aktuelles", "orte"] });
  // Ein Beitrag an einem Projekt zählt im Reiter „Fragen" der Akte mit.
  if (b.projekt_id) void qc.invalidateQueries({ queryKey: schluessel.projekt(b.projekt_id) });
}

export function useBeitragNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: BeitragNeu) => api.post<Beitrag>("/aktuelles", b),
    onSuccess: (b) => legeBeitragAb(qc, b),
  });
}

export function useBeitragAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: BeitragAendern & { id: string }) => api.patch<Beitrag>(`/aktuelles/${id}`, b),
    onSuccess: (b) => legeBeitragAb(qc, b),
  });
}

export function useBeitragLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/aktuelles/${id}`),
    onSuccess: (_d, id) => {
      // Der Satz ist fort — ihn stehen zu lassen hieße, dass ein direkter
      // Aufruf den gelöschten Beitrag aus dem Zwischenspeicher zeigt.
      qc.removeQueries({ queryKey: aktuellesSchluessel.beitrag(id) });
      void qc.invalidateQueries({ queryKey: aktuellesSchluessel.listen });
      void qc.invalidateQueries({ queryKey: ["aktuelles", "neueste"] });
      void qc.invalidateQueries({ queryKey: ["aktuelles", "orte"] });   // §37.10
    },
  });
}

/**
 * `POST|DELETE /aktuelles/{id}/freigabe` — „öffentlich" setzen und wegnehmen
 * (nur Admin/GF). Fehlt einem Medium der Bildnachweis, antwortet der Server
 * 422 mit dem Satz „Jedes Bild braucht einen Bildnachweis." — der Aufrufer
 * zeigt ihn unverändert, denn er nennt genau die fehlende Handlung.
 */
export function useFreigabe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, an }: { id: string; an: boolean }) =>
      an ? api.post<Beitrag>(`/aktuelles/${id}/freigabe`) : api.del<Beitrag>(`/aktuelles/${id}/freigabe`),
    onSuccess: (b) => {
      legeBeitragAb(qc, b);
      // Das Schaufenster liest eine eigene Liste.
      void qc.invalidateQueries({ queryKey: aktuellesSchluessel.oeffentlich });
    },
  });
}

/** `POST /aktuelles/{id}/beantwortet` — `null` nimmt die Markierung zurück. */
export function useBeantwortet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, kommentar_id }: { id: string; kommentar_id: string | null }) =>
      api.post<Beitrag>(`/aktuelles/${id}/beantwortet`, { kommentar_id }),
    onSuccess: (b) => legeBeitragAb(qc, b),
  });
}

/* ---- Medien ----
   Derselbe multipart-Weg wie `POST /projekte/{id}/bilder` (SPEC §16.2). Eine
   Auswahl AUS DER PROJEKTGALERIE schickt statt der Datei die Kennung des
   vorhandenen Bildes (`bild_id`) — der Server legt dann ein `beitrag_medium`
   auf dieselbe `datei_id` und lässt die Ableitung in Ruhe. */

export function useMedienHochladen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      beitragId,
      dateien,
      bildIds,
      nachweis,
    }: {
      beitragId: string;
      dateien?: File[];
      bildIds?: string[];
      /** Gilt für ALLES, was dieser EINE Aufruf anhängt (SPEC §16.5). */
      nachweis?: string;
    }) => {
      const fd = new FormData();
      for (const d of dateien ?? []) fd.append("dateien", d);
      for (const id of bildIds ?? []) fd.append("bild_id", id);
      if (nachweis?.trim()) fd.append("nachweis", nachweis.trim());
      return api.upload<Beitrag>(`/aktuelles/${beitragId}/medien`, fd);
    },
    // Der Aufruf ist alles oder nichts (§16.5): eine unbekannte Bildkennung
    // hängt GAR nichts an, statt die davor verarbeiteten stehen zu lassen. Ein
    // Fehlschlag lässt den Beitrag also unverändert, und wer davor einen
    // eigenen Zustand gedreht hat, darf ihn bedenkenlos zurückrollen.
    onSuccess: (b) => legeBeitragAb(qc, b),
  });
}

export function useMediumAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: { id: string; nachweis?: string; sortierung?: number }) =>
      api.patch<Beitrag>(`/aktuelles/medien/${id}`, b),
    onSuccess: (b) => legeBeitragAb(qc, b),
  });
}

export function useMediumLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<Beitrag>(`/aktuelles/medien/${id}`),
    onSuccess: (b) => legeBeitragAb(qc, b),
  });
}

/* ---- Kommentare ---- */

export function useKommentarNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ beitragId, ...b }: KommentarNeu & { beitragId: string }) =>
      api.post<Beitrag>(`/aktuelles/${beitragId}/kommentare`, b),
    onSuccess: (b) => legeBeitragAb(qc, b),
  });
}

export function useKommentarLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<Beitrag>(`/aktuelles/kommentare/${id}`),
    onSuccess: (b) => legeBeitragAb(qc, b),
  });
}

/* ---- Reaktionen (optimistisch) ---- */

/** Zähler und eigene Marke nach einem Klick — ohne je unter null zu fallen. */
function reaktionenNach(r: Reaktionen, art: ReaktionArt, an: boolean): Reaktionen {
  if (r.meine.includes(art) === an) return r;
  const d = an ? 1 : -1;
  return {
    applaus: Math.max(0, r.applaus + (art === "applaus" ? d : 0)),
    herz: Math.max(0, r.herz + (art === "herz" ? d : 0)),
    pflanze: Math.max(0, r.pflanze + (art === "pflanze" ? d : 0)),
    meine: an ? [...r.meine, art] : r.meine.filter((x) => x !== art),
    // `wer` dreht der Klick nicht mit: die eigene Kennung kennt diese Stelle
    // nicht, und die Serverantwort kommt einen Augenblick später ohnehin.
    wer: r.wer,
  };
}

/** Einen Beitrag (kurz oder voll) mit der neuen Reaktion — unverändert, wenn er nicht gemeint ist. */
function beitragMitReaktion<T extends BeitragKurz>(b: T, z: ReaktionSetzen, wert: Reaktionen | null): T {
  let neu = b;
  if (z.ziel_art === "beitrag" && b.id === z.ziel_id) {
    neu = { ...neu, reaktionen: wert ?? reaktionenNach(b.reaktionen, z.art, z.an) };
  }
  const komm = (neu as unknown as Beitrag).kommentare;
  if (z.ziel_art === "kommentar" && Array.isArray(komm) && komm.some((k) => k.id === z.ziel_id)) {
    neu = {
      ...neu,
      kommentare: komm.map((k) => (k.id === z.ziel_id ? { ...k, reaktionen: wert ?? reaktionenNach(k.reaktionen, z.art, z.an) } : k)),
    } as T;
  }
  return neu;
}

/**
 * Jeden abgelegten Satz unter `["aktuelles"]` durchgehen. Die Form ist
 * entweder eine Seitensammlung (`{pages: AktuellesSeite[]}`) oder ein
 * einzelner `Beitrag`; alles andere bleibt, wie es ist.
 */
function reaktionImSpeicher(alt: unknown, z: ReaktionSetzen, wert: Reaktionen | null): unknown {
  if (!alt || typeof alt !== "object") return alt;
  const seiten = (alt as { pages?: unknown }).pages;
  if (Array.isArray(seiten)) {
    return {
      ...(alt as object),
      pages: (seiten as AktuellesSeite[]).map((s) => ({ ...s, beitraege: s.beitraege.map((b) => beitragMitReaktion(b, z, wert)) })),
    };
  }
  const b = alt as Partial<Beitrag>;
  if (typeof b.id === "string" && b.reaktionen) return beitragMitReaktion(alt as Beitrag, z, wert);
  return alt;
}

export function useReaktionSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: ReaktionSetzen) => api.put<Reaktionen>("/aktuelles/reaktionen", b),
    onMutate: async (z) => {
      // Laufende Abfragen abbrechen, sonst überschreibt eine gerade
      // eintreffende alte Antwort die optimistische Drehung.
      await qc.cancelQueries({ queryKey: aktuellesSchluessel.alle });
      const vorher = qc.getQueriesData({ queryKey: aktuellesSchluessel.alle });
      qc.setQueriesData({ queryKey: aktuellesSchluessel.alle }, (alt: unknown) => reaktionImSpeicher(alt, z, null));
      return { vorher };
    },
    onError: (_fehler, _z, ctx) => {
      for (const [k, d] of ctx?.vorher ?? []) qc.setQueryData(k, d);
    },
    onSuccess: (antwort, z) => {
      // Die Serverzahlen sind die Wahrheit: nebenan klatscht jemand mit.
      qc.setQueriesData({ queryKey: aktuellesSchluessel.alle }, (alt: unknown) => reaktionImSpeicher(alt, z, antwort));
    },
  });
}

/* ---- Gelesen-Marke ---- */

/** `POST /aktuelles/gesehen` — setzt `users.aktuelles_gesehen_am` auf jetzt. */
export function useGesehenMelden() {
  return useMutation({
    mutationFn: () => api.post<void>("/aktuelles/gesehen"),
    // Läuft beim bloßen Öffnen von Aktuelles — keine Handlung, also kein
    // Anlass, ein neues Abzeichen zu feiern (Wache in `startseite.ts`).
    meta: KEINE_HANDLUNG,
  });
}

/* ---- Die Projektrunde (§18.5, Wanderungen 031/032) ----
   Jede Änderung an einem Punkt entwertet die GANZE Runde und legt nicht die eine
   Zeile ab: das Abhaken eines Punktes verschiebt die Zahl „3 offen" im Kopf, und
   der Abschluss friert alle Punkte auf einmal ein. Ein Client, der nur die
   geänderte Zeile ersetzte, zeigte den alten Kopf über der neuen Liste.

   §39.11: jede Änderung hier entwertet dazu JEDES Projekt-Cockpit (`["cockpit"]`).
   Offen, Beschlüsse, nächster Schritt, Puls und Verlauf rechnen aus den Punkten, und
   der Start verschiebt die Verlauf-Zeit auf `begonnen_am`. Wer danach ins Projektblatt
   wechselt, sähe sonst bis zu 30 s (`FRISCH_MS`) ein altes Cockpit, sobald der
   Ereignisstrom hängt (Telefon im Hintergrund, Neustart des Backends). Die Meldung
   der Runde trägt keine Projektkennung (031) — gezielter geht es nicht. */

export function useRunden(bueroId?: string, status?: string, aktiv = true): UseQueryResult<RundeKurz[]> {
  return useQuery({
    queryKey: schluessel.runden(bueroId, status),
    queryFn: () => api.get<RundeKurz[]>("/runden" + frage({ buero_id: bueroId, status })),
    enabled: aktiv,
    staleTime: 30_000,
  });
}

export function useRunde(id: string | null): UseQueryResult<Runde> {
  return useQuery({
    queryKey: schluessel.runde(id ?? ""),
    queryFn: () => api.get<Runde>(`/runden/${id}`),
    enabled: !!id,
    staleTime: 10_000,
  });
}

/**
 * `GET /me/runde` — die nächste Runde eines meiner Büros, darin nur meine Punkte.
 *
 * `null` ist hier eine ANTWORT und kein Fehler: wer in einem Büro ohne Rundentag
 * sitzt, bekommt sie nie, und das ist kein Zustand, den der Bildschirm als Panne
 * zeigen darf.
 */
export function useMeineRunde(aktiv = true): UseQueryResult<Runde | null> {
  return useQuery({
    queryKey: schluessel.meineRunde,
    // `null` ist eine Antwort („keine Runde für dich"), kein Fehler. Der Client macht aus
    // einem JSON-`null` aber `undefined`, und das wertet TanStack Query v5 als Fehlschlag —
    // die Karte zeigte dann „ließ sich nicht laden" für jeden ohne Runde.
    queryFn: async () => (await api.get<Runde | null>("/me/runde")) ?? null,
    enabled: aktiv,
    staleTime: 30_000,
  });
}

export function useRundeAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { buero_id: string; datum: string; notiz?: string }) =>
      api.post<Runde>("/runden", b),
    onSuccess: (r) => {
      qc.setQueryData(schluessel.runde(r.id), r);
      void qc.invalidateQueries({ queryKey: schluessel.rundenAlle });
      void qc.invalidateQueries({ queryKey: schluessel.cockpitAlle });
    },
  });
}

/** `start` und `abschluss` gehen denselben Weg — beide geben die ganze Runde zurück. */
export function useRundeSchritt(rundeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weg: "start" | "abschluss") =>
      api.post<Runde>(`/runden/${rundeId}/${weg}`, {}),
    onSuccess: (r) => {
      qc.setQueryData(schluessel.runde(r.id), r);
      void qc.invalidateQueries({ queryKey: schluessel.rundenAlle });
      void qc.invalidateQueries({ queryKey: schluessel.cockpitAlle });
    },
  });
}

export function useRundePunktAnlegen(rundeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: RundePunktNeu) => api.post<Runde>(`/runden/${rundeId}/punkte`, b),
    onSuccess: (r) => {
      qc.setQueryData(schluessel.runde(r.id), r);
      void qc.invalidateQueries({ queryKey: schluessel.rundenAlle });
      void qc.invalidateQueries({ queryKey: schluessel.cockpitAlle });
    },
  });
}

/**
 * `PATCH /runden/punkte/{id}` — der Punkt kommt einzeln zurück, entwertet aber die
 * ganze Runde (siehe der Block oben).
 *
 * Mit `erledigt` ALLE Runden (`["runden"]`), nicht nur diese: seit §39.4 nimmt der
 * Haken den noch nicht besprochenen Nachfolger in der Runde der Folgewoche mit (und
 * „Rückgängig" gibt ihn zurück) — das ist eine andere Runde als `rundeId`.
 */
export function useRundePunktAendern(rundeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ punktId, ...b }: RundePunktAendern & { punktId: string }) =>
      api.patch<RundePunkt>(`/runden/punkte/${punktId}`, b),
    onSuccess: (_punkt, { erledigt }) => {
      if (erledigt !== undefined) {
        void qc.invalidateQueries({ queryKey: schluessel.rundenAlle });
      } else {
        void qc.invalidateQueries({ queryKey: schluessel.runde(rundeId) });
        void qc.invalidateQueries({ queryKey: schluessel.meineRunde });
      }
      void qc.invalidateQueries({ queryKey: schluessel.cockpitAlle });
    },
  });
}

/** `POST /runden/{id}/protokoll` — legt den Beitrag in Aktuelles an (§16). */
export function useRundeProtokoll(rundeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ id: string }>(`/runden/${rundeId}/protokoll`, {}),
    onSuccess: () => {
      // Beides: die Runde trägt jetzt die Kennung des Beitrags, und der Feed hat
      // einen Beitrag mehr.
      void qc.invalidateQueries({ queryKey: schluessel.rundenAlle });
      void qc.invalidateQueries({ queryKey: ["aktuelles"] });
    },
  });
}

/* ---- Abwesenheit (§18.5) ---- */

export function useAbwesenheiten(von?: string, bis?: string): UseQueryResult<Abwesenheit[]> {
  return useQuery({
    queryKey: schluessel.abwesenheiten(von, bis),
    queryFn: () => api.get<Abwesenheit[]>("/abwesenheiten" + frage({ von, bis })),
    staleTime: 60_000,
  });
}

export function useMeineAbwesenheit(): UseQueryResult<Abwesenheit[]> {
  return useQuery({
    queryKey: schluessel.meineAbwesenheit,
    queryFn: () => api.get<Abwesenheit[]>("/me/abwesenheit"),
    staleTime: 60_000,
  });
}

export function useAbwesenheitSetzen() {
  const qc = useQueryClient();
  const entwerte = () => {
    void qc.invalidateQueries({ queryKey: schluessel.meineAbwesenheit });
    void qc.invalidateQueries({ queryKey: ["abwesenheiten"] });
  };
  return {
    anlegen: useMutation({
      mutationFn: (b: { von: string; bis: string; art?: string; notiz?: string }) =>
        api.post<Abwesenheit>("/me/abwesenheit", b),
      onSuccess: entwerte,
    }),
    loeschen: useMutation({
      mutationFn: (id: string) => api.del<void>(`/me/abwesenheit/${id}`),
      onSuccess: entwerte,
    }),
  };
}
